import { Getter } from '../Getter';
import { HttpError } from '../HttpError';
import { ApiMetricsHighlights } from './Models/ApiMetricsHighlights';
import { MetricsHighlights } from './Models/MetricsHighlights';
import isValidUUID from 'uuid-validate';
import URI from 'urijs';

type ApiResponse = ApiMetricsHighlights | null | undefined;

interface DataResponse {
  data?: MetricsHighlights;
  error?: never;
}
interface ErrorResponse {
  data?: never;
  error: HttpError;
}
export type ClientOutput = DataResponse | ErrorResponse;

interface ClientInput {
  organizationUUID: string;
  repositoryUUID?: string;
}

export class MetricsHighlightsClient {
  private getter: Getter<ApiResponse | HttpError>;

  constructor(getter: Getter<ApiResponse | HttpError>) {
    this.getter = getter;
  }

  public async retrieveAsync(input: ClientInput): Promise<ClientOutput> {
    const { organizationUUID, repositoryUUID } = input;

    if (
      !isValidUUID(organizationUUID) ||
      (repositoryUUID && !isValidUUID(repositoryUUID))
    ) {
      // equivalent to the response we'd get on the server
      return {
        error: new HttpError(400),
      };
    }

    const response = await this.tryRetrieveAsync(input);

    if (!response) {
      return {
        data: undefined,
        error: undefined,
      };
    }

    if (response instanceof HttpError) {
      return {
        error: response,
      };
    }

    if (response.version !== 1) {
      throw new Error(
        `Only version 1 of the '${this.getHref(
          input
        )}' payload body is supported`
      );
    }
    return {
      data: this.toMetricsHighlights(response),
    };
  }

  private async tryRetrieveAsync(
    input: ClientInput
  ): Promise<ApiResponse | HttpError> {
    const href = this.getHref(input);

    return await this.getter.get(href);
  }

  private toMetricsHighlights(response: ApiMetricsHighlights) {
    const { period, time, highlights } = response;

    const reviewedLines = highlights.distributions.find(
      (dist) => dist.metric === 'total_line_counts'
    );

    const authorTests = highlights.distributions.find(
      (dist) => dist.metric === 'total_test_line_counts'
    );

    return {
      period: {
        type: period.type,
        from: new Date(period.from),
        value: period.value,
      },
      insightCreatedDate: new Date(time),
      listNumLinesByRepository: highlights.repository.order,
      listPercentageTestsByAuthors: highlights.author.order,
      listNumIssuesCaughtByReviewers: highlights.reviewer.order,
      reviewedLinesDistribution: {
        percentReviewers: reviewedLines?.group_percent,
        percentCode: reviewedLines?.metric_percent,
      },
      authorTestsDistribution: {
        percentAuthors: authorTests?.group_percent,
        percentTests: authorTests?.metric_percent,
      },
    };
  }

  private getHref({ organizationUUID, repositoryUUID }: ClientInput): string {
    const href = URI('/api/v1/organizations/')
      .segmentCoded(organizationUUID)
      .segment('benchmark-insight')
      .segment('metrics_highlights');

    if (repositoryUUID) {
      href.search({ repository: repositoryUUID });
    }
    return href.toString();
  }
}
